import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, FileDown, RefreshCcwIcon } from 'lucide-react';
import { cn, formatPrice } from '@shared/utils/utils';
import { Button } from '@shared/components/common/Button';
import { ROUTES } from '@shared/configs/constants';
import ProductItem from './ProductItem';
import { PurchaseOrder } from '@shared/types/order';
import { ORDER_STATUS_COLORS, ORDER_STATUS_LABELS } from '@shared/components/constants/select.constant';
import { DATE_TIME_YMD } from '@shared/components/constants/common.constants';
import { formatDateTime } from '@shared/utils/datetime';
import { ORDER_STATUS } from '@shared/types/enums';
import { useStore } from '@shared/hooks/useStore';
import { useRepayment } from '@shared/hooks/useRepayment';
import { flowResult } from 'mobx';

interface OrderCardProps {
    order: PurchaseOrder
}

export default function OrderCard({ order }: OrderCardProps) {
    const navigate = useNavigate();
    const { orderStore, modalStore } = useStore();
    const handleRepayment = useRepayment();
    const [isExpanded, setIsExpanded] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (contentRef.current) {
            if (isExpanded) {
                // Measure and set height when expanding
                setContentHeight(contentRef.current.scrollHeight);
            }
        }
    }, [isExpanded, order.orderDetails]);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const handleViewDetail = () => {
        navigate(ROUTES.purchaseHistoryDetail.href.replace(':id', order.id.toString()));
    };

    const onRepayment = () => {
        handleRepayment(order.id);
    };

    const handleExportInvoice = async () => {
        if (!order?.id) return;

        try {
            const result = await flowResult(orderStore.exportInvoicePDF(order.id));
            if (!result?.success) {
                modalStore.showAlertModal({
                    id: 'export-invoice-error',
                    type: 'error',
                    size: 'alert',
                    content: '支払明細書のダウンロードに失敗しました。',
                    cancelButton: false,
                    saveButton: '閉じる'
                });
            }
        } catch (error) {
            console.error('Error exporting invoice:', error);
        }
    };

    return (
        <article className='bg-white w-full rounded-lg shadow p-4 md:p-6 hover:shadow-md transition-shadow'>
            <div className='flex flex-col'>
                {/* Top row: Order ID, Status, Amount */}
                <div className='flex items-center justify-between flex-wrap gap-2'>
                    <div className='flex items-center gap-2.5 flex-wrap'>
                        <span className='text-[#009ACD] font-bold text-base md:text-xl'>{order.code}</span>
                        <span className={cn('px-3 py-1 rounded-full text-sm font-semibold', ORDER_STATUS_COLORS[order.status])}>
                            {ORDER_STATUS_LABELS[order.status]}
                        </span>
                    </div>
                    <span className='text-xl font-bold'>
                        {formatPrice(Number(order.total_amount))}
                    </span>
                </div>

                {/* Bottom row: Order Date, View Details */}
                <div className='flex items-center justify-between mt-3'>
                    <span className='text-text-secondary text-sm font-medium'>
                        注文日
                        {' '}
                        {formatDateTime(order.order_at, DATE_TIME_YMD)}
                    </span>
                    <button
                        onClick={toggleExpand}
                        className='flex items-center gap-1 text-text-secondary text-sm hover:text-primary transition-colors'
                    >
                        <span>詳細を見る</span>
                        <ChevronDown
                            size={16}
                            className={cn('transition-transform duration-200', isExpanded && 'rotate-180')}
                        />
                    </button>
                </div>

                {/* Expanded Order Details */}
                <div
                    className='overflow-hidden transition-all duration-300 ease-in-out'
                    style={{
                        maxHeight: isExpanded && order.orderDetails && order.orderDetails.length > 0 ? `${contentHeight + 36}px` : '0px',
                        opacity: isExpanded && order.orderDetails && order.orderDetails.length > 0 ? 1 : 0
                    }}
                >
                    <hr className='my-4' />
                    <div ref={contentRef}>
                        {/* Product List Section */}
                        <div className='space-y-3'>
                            <h3 className='font-medium'>商品名</h3>
                            <ul className='space-y-3'>
                                {(order.orderDetails || []).map(publicationItem => (
                                    <li key={publicationItem.id}>
                                        <ProductItem product={publicationItem} />
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Payment Summary Section */}
                        <hr className='my-3' />

                        <ul className='space-y-3'>
                            <li className='flex justify-between text-sm'>
                                <span className='text-text-secondary'>税別支払金額</span>
                                <span className='font-medium'>{formatPrice(Number(order.pre_amount) || 0)}</span>
                            </li>
                            <li className='flex justify-between text-sm'>
                                <span className='text-text-secondary'>消費税（10%）</span>
                                <span className='font-medium'>{formatPrice(Number(order.tax) || 0)}</span>
                            </li>
                            <li className='flex justify-between text-sm'>
                                <span className='text-text-secondary'>お支払い方法</span>
                                <span className='font-medium'>{order.payment_method || '-'}</span>
                            </li>
                            <li className='flex justify-between text-base pt-2 border-t border-gray-200'>
                                <span className=''>お支払い金額</span>
                                <span className='font-bold'>{formatPrice(Number(order.total_amount))}</span>
                            </li>
                        </ul>

                        <hr className='my-3' />

                        {/* Action Buttons */}
                        <div className='flex gap-3'>
                            {order.status === ORDER_STATUS.PAID ?

                                (
                                    <Button
                                        className='px-3 flex-1 text-base gap-2'
                                        size='lg'
                                        onClick={handleExportInvoice}
                                    >
                                        <FileDown size={18} />
                                        支払明細書
                                    </Button>
                                ) :
                                (
                                    <Button
                                        className='px-3 flex-1 text-base gap-2 bg-[#FFB145] hover:bg-[#FFB145]/90'
                                        size='lg'
                                        onClick={onRepayment}
                                    >
                                        <RefreshCcwIcon size={18} />
                                        再支払
                                    </Button>
                                )}
                            <Button
                                variant='outline'
                                className='flex-1 px-3 text-base border-primary text-primary hover:bg-primary hover:text-white'
                                size='lg'
                                onClick={handleViewDetail}
                            >
                                <span>詳細を見る</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
}
